import { createStore, get as idbGet, set as idbSet, del as idbDel } from "idb-keyval";
import { AxiosError } from "axios";
import { merge3 } from "@/lib/draftStore";
import { GLOBAL, GLOBAL_EMPTY, QUEUEABLE, EMPTY_OK, EMPTY_OBJ, PATH_FALLBACK, NOUN, matches } from "@/lib/offline/rules";
import { segs, splitUrl, walk, findById, synth, replaceIds, replaceUrl } from "@/lib/offline/cache";

const S = { user: null, store: null, meta: {}, outbox: [], idmap: {}, unis: {}, index: {}, manual: localStorage.getItem("lf_offline_manual") === "1", auto: false, busy: {}, pushing: false, ready: Promise.resolve() };
const listeners = new Set();
let snap = null;
const emit = () => { snap = null; listeners.forEach((l) => l()); };
export const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
export const getSnapshot = () => {
  if (!snap) snap = { offline: offlineMode(), local: S.outbox.length > 0, manual: S.manual, auto: S.auto, meta: S.meta, outbox: S.outbox, busy: S.busy, pushing: S.pushing, eligible: eligible(), user: S.user };
  return snap;
};

const timers = {};
const persist = (k) => {
  clearTimeout(timers[k]);
  timers[k] = setTimeout(() => {
    if (!S.store) return;
    const v = k === "meta" ? S.meta : k === "outbox" ? S.outbox : k === "idmap" ? S.idmap : S.unis[k.slice(2)];
    (v ? idbSet(k, v, S.store) : idbDel(k, S.store)).catch(() => {});
  }, k.startsWith("u:") ? 800 : 50);
};

const reindex = (uid) => {
  const r = S.unis[uid]?.r || {};
  Object.values(r).forEach((v) => walk(v, (o) => { if (typeof o.id === "string") S.index[o.id] = uid; }));
};

export const bindUser = (uid) => {
  if (!uid || uid === S.user) return S.ready;
  S.user = uid;
  localStorage.setItem("lf_offline_user", uid);
  S.store = createStore(`loreright-offline-${uid}`, "kv");
  S.ready = (async () => {
    S.meta = (await idbGet("meta", S.store).catch(() => null)) || {};
    S.outbox = (await idbGet("outbox", S.store).catch(() => null)) || [];
    S.idmap = (await idbGet("idmap", S.store).catch(() => null)) || {};
    S.unis = {}; S.index = {};
    for (const id of ["_g", ...Object.keys(S.meta)]) {
      const u = await idbGet(`u:${id}`, S.store).catch(() => null);
      if (u) { S.unis[id] = u; reindex(id); }
    }
    emit();
  })();
  return S.ready;
};
if (localStorage.getItem("lf_offline_user")) bindUser(localStorage.getItem("lf_offline_user"));

const ELIGIBLE = ["admin", "lifetime", "subscription", "granted"];
export const eligible = () => ELIGIBLE.includes(S.unis._g?.r?.["/billing/me"]?.base_source);
export const offlineMode = () => S.manual || S.auto || !navigator.onLine;
const hasMeta = () => Object.keys(S.meta).length > 0;
export const pendingFor = (uid) => S.outbox.filter((e) => !uid || e.uid === uid);
export const isLocal = (uid) => !!(uid && S.meta[uid] && (eligible() || pendingFor(uid).length) && (offlineMode() || pendingFor(uid).length));

const uidOf = (path) => {
  const s = segs(path);
  if (s[0] === "universes" && s[1] && S.meta[s[1]]) return s[1];
  return (s[1] && S.index[s[1]]) || null;
};

const ok = (config, data, status = 200) => ({ data, status, statusText: "OK", headers: {}, config, request: {} });
const fail = (config, detail) => {
  const response = { data: { detail, code: "offline" }, status: 503, statusText: "Offline", headers: {}, config };
  return new AxiosError(detail, "ERR_OFFLINE", config, null, response);
};
const clone = (v) => (v instanceof Blob ? v : v === undefined ? v : structuredClone(v));

const lookup = (uid, path, key, params) => {
  if (!uid) {
    if (GLOBAL_EMPTY.has(path)) return [];
    const v = S.unis._g?.r?.[key] ?? S.unis._g?.r?.[path];
    return path === "/universes" && Array.isArray(v) ? v.filter((u) => S.meta[u.id]) : v;
  }
  const r = S.unis[uid]?.r || {};
  if (key in r) return r[key];
  if (matches(EMPTY_OK, path)) return [];
  if (matches(EMPTY_OBJ, path)) return {};
  if (/^\/timelines\/[^/]+\/snapshot$/.test(path)) return { entities: [], events_at_moment: [], offline: true };
  if (matches(PATH_FALLBACK, path) && path in r) {
    const v = r[path];
    if (/\/stats$/.test(path) && Array.isArray(v?.hours)) return { ...v, hours: v.hours.filter((h) => (!params.start || h.hour >= params.start) && (!params.end || h.hour <= params.end)) };
    if (params.q && Array.isArray(v)) { const q = String(params.q).toLowerCase(); return v.filter((e) => JSON.stringify([e.name, e.title, e.summary, e.aliases, e.text, e.lines]).toLowerCase().includes(q)); }
    return v;
  }
  return undefined;
};

const touch = (uid) => { persist(`u:${uid}`); };

const asList = (v, k) => (Array.isArray(v) ? v : Array.isArray(v?.[k]) ? v[k] : undefined);

const applyMutation = (uid, method, path, body, server, tmp) => {
  const r = S.unis[uid].r;
  const s = segs(path);
  const last = s[s.length - 1];
  if (method === "delete") {
    if (s[0] === "universes" && s[2] === "categories") {
      const name = decodeURIComponent(s[3]);
      const u = r[`/universes/${uid}`];
      if (u?.custom_categories) u.custom_categories = u.custom_categories.filter((c) => c.name !== name);
      Object.values(r).forEach((v) => walk(v, (o) => { if (o.category === name && o.universe_id === uid) o.category = "Lore"; }));
    } else {
      Object.values(r).forEach((v) => walk(v, (o, parent, k) => { if (o.id === last && Array.isArray(parent)) parent.splice(k, 1); }));
      Object.keys(r).forEach((k) => { if (k === path || k.startsWith(`${path}/`) || k.startsWith(`${path}?`)) delete r[k]; });
    }
    touch(uid);
    return { ok: true };
  }
  if (method === "patch" || method === "put") {
    let out = body;
    if (s[0] === "drafts" && last === "order") {
      const list = asList(r[`/drafts/${s[1]}/items`], "items");
      if (Array.isArray(list)) { list.sort((a, b) => body.ids.indexOf(a.id) - body.ids.indexOf(b.id)); list.forEach((it, i) => { it.order = i; }); }
      out = { ok: true };
    } else if (last === "relationship-layout") {
      (r[`/universes/${uid}/relationship-web`]?.nodes || []).forEach((n) => { if (body.positions?.[n.id]) n.pos = body.positions[n.id]; });
      out = { ok: true };
    } else if (last === "relationship-types") {
      const web = r[`/universes/${uid}/relationship-web`];
      const types = [...(web?.types || []).filter((t) => !t.custom), ...(body.types || []).map((t) => ({ ...t, custom: true }))];
      if (web) web.types = types;
      out = { types };
    } else {
      const id = ["grid", "meta", "images"].includes(last) || (s[0] === "languages" && last === "order") ? s[1] : last;
      const patch = server && typeof server === "object" && server.id === id ? server : body;
      Object.values(r).forEach((v) => walk(v, (o) => { if (o.id === id) Object.assign(o, patch); }));
      if (s[0] === "universes") (S.unis._g?.r?.["/universes"] || []).forEach((u) => { if (u.id === id) Object.assign(u, patch); });
      out = { ...(findById(r, id) || {}), ...patch };
    }
    touch(uid);
    return clone(out);
  }
  if (s[0] === "universes" && last === "categories") {
    const u = r[`/universes/${uid}`];
    if (u) u.custom_categories = [...(u.custom_categories || []), { name: body.name, color: body.color }];
    touch(uid);
    return { name: body.name, color: body.color };
  }
  const targets = last === "events" && s[0] === "universes" ? [`/timelines/${body.timeline_id}/events`, `/universes/${uid}/events/all`] : [path];
  const lists = targets.map((t) => asList(r[t], last)).filter(Array.isArray);
  const detailRe = new RegExp(`^/${last}/[^/?]+$`);
  const detailKey = Object.keys(r).find((k) => detailRe.test(k) && r[k] && typeof r[k] === "object" && !Array.isArray(r[k]));
  const obj = server && typeof server === "object" && server.id ? server : synth(lists[0]?.[0] || (detailKey && r[detailKey]) || {}, path, body, tmp, uid);
  lists.forEach((l) => { if (!l.some((x) => x.id === obj.id)) l.push(clone(obj)); });
  if (!lists.length) {
    const parent = findById(r, s[1]);
    if (parent && Array.isArray(parent[last])) parent[last].push(clone(obj));
  }
  if (detailKey) {
    const d = { ...synth(r[detailKey], path, {}, obj.id, uid), ...clone(obj) };
    if (s[0] === "drafts" && last === "items") {
      const wrap = r[path];
      const draftDoc = wrap?.draft || findById(r, s[1]);
      if (draftDoc) { d.draft = clone(draftDoc); d.story = clone(r[`/stories/${draftDoc.story_id}`] || findById(r, draftDoc.story_id) || d.story); }
      d.chapters = (lists[0] || []).filter((x) => x.kind === "chapter").map((x) => ({ id: x.id, title: x.title }));
    }
    r[`/${last}/${obj.id}`] = d;
  }
  S.index[obj.id] = uid;
  touch(uid);
  return clone(obj);
};

const labelFor = (uid, method, path, body, before) => {
  const s = segs(path);
  const noun = NOUN[s[s.length - 1]] || NOUN[s[0]] || s[0];
  const name = body?.name || body?.title || before?.name || before?.title || body?.word || before?.word || "";
  const verb = method === "post" ? "Created" : method === "delete" ? "Deleted" : "Edited";
  return `${verb} ${noun}${name ? ` “${String(name).slice(0, 60)}”` : ""}`;
};

const enqueue = (uid, method, path, body, before, tmp) => {
  const tmpInPath = segs(path).find((p) => p.startsWith("tmp-"));
  if (method === "delete" && tmpInPath) {
    S.outbox = S.outbox.filter((e) => !(e.path.includes(tmpInPath) || e.tmp === tmpInPath || JSON.stringify(e.body || {}).includes(tmpInPath)));
  } else {
    const same = (method === "patch" || method === "put") && S.outbox.find((e) => e.uid === uid && e.method === method && e.path === path);
    const base = before && method === "patch" ? Object.fromEntries(Object.keys(body || {}).map((k) => [k, before[k]])) : null;
    if (same) Object.assign(same, { body: { ...same.body, ...body }, base: same.base || base ? { ...(base || {}), ...(same.base || {}) } : null, ts: Date.now(), error: null });
    else S.outbox = [...S.outbox, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, uid, method, path, body, base, tmp, label: labelFor(uid, method, path, body, before), ts: Date.now() }];
  }
  S.outbox = [...S.outbox];
  persist("outbox");
  emit();
};

const parseBody = (data) => {
  if (data == null || data === "") return {};
  if (typeof data === "string") { try { return JSON.parse(data); } catch { return null; } }
  return data instanceof FormData ? null : data;
};

const serveLocal = (config, method, path, key, uid, params) => {
  if (method === "get") {
    const v = lookup(uid, path, key, params);
    if (v === undefined) throw fail(config, "This isn't available offline. Connect to the internet to open it.");
    return ok(config, clone(v));
  }
  const body = parseBody(config.data);
  if (!uid || body === null || !matches(QUEUEABLE[method], path)) {
    throw fail(config, navigator.onLine && !S.manual && !S.auto ? "Push your offline changes first to use this online feature." : "This needs an internet connection.");
  }
  const r = S.unis[uid].r;
  const s = segs(path);
  const before = method === "patch" ? clone(findById(r, s[s.length - 1])) : method === "delete" ? findById(r, s[s.length - 1]) : null;
  const tmp = method === "post" ? `tmp-${crypto.randomUUID?.() || Date.now().toString(36)}` : null;
  const res = applyMutation(uid, method, path, body, null, tmp);
  enqueue(uid, method, path, body, before, method === "post" && res?.id === tmp ? tmp : null);
  return ok(config, res, method === "post" ? 201 : 200);
};

let pingT = null;
const startPing = () => {
  if (pingT) return;
  pingT = setInterval(async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/`, { cache: "no-store" });
      if (r.ok) { S.auto = false; clearInterval(pingT); pingT = null; emit(); window.dispatchEvent(new Event("lf-offline-mode")); refreshStale(); }
    } catch { /* still offline */ }
  }, 15000);
};

const record = (id, key, data) => {
  if (!S.unis[id]) S.unis[id] = { r: {} };
  S.unis[id].r[key] = data instanceof Blob ? data : structuredClone(data);
  if (id !== "_g") reindex(id);
  else if (key === "/billing/me") emit();
  persist(`u:${id}`);
};

export const offlineAdapter = (base) => async (config) => {
  await S.ready;
  const method = (config.method || "get").toLowerCase();
  if (config._online || !S.user || window.location.pathname.startsWith("/p/") || window.location.pathname.startsWith("/s/")) return base(config);
  const { path, key, params } = splitUrl(config.url, config.params);
  const g = GLOBAL.has(path);
  const uid = g ? null : uidOf(path);
  const local = g ? method === "get" && offlineMode() && hasMeta() && eligible() : isLocal(uid);
  if (local) return serveLocal(config, method, path, key, uid, params);
  try {
    const res = await base(config);
    const data = typeof res.data === "string" ? (() => { try { return JSON.parse(res.data); } catch { return res.data; } })() : res.data;
    if (method === "get" && g && (hasMeta() || path === "/billing/me")) record("_g", key, data);
    else if (method === "get" && uid && S.meta[uid]) record(uid, key, data);
    else if (uid && S.meta[uid] && matches(QUEUEABLE[method], path)) { try { applyMutation(uid, method, path, parseBody(config.data) || {}, data, null); } catch { /* cache refreshes later */ } }
    else if (uid && S.meta[uid] && method !== "get") { S.meta[uid] = { ...S.meta[uid], stale: true }; persist("meta"); }
    return res;
  } catch (e) {
    const net = !e.response && e.code !== "ERR_CANCELED";
    if (net && eligible() && ((g && method === "get" && hasMeta()) || (uid && S.meta[uid]))) {
      S.auto = true; emit(); startPing();
      return serveLocal(config, method, path, key, uid, params);
    }
    throw e;
  }
};

let api = null;
export const attachApi = (a) => { api = a; };
const online = (url, opts = {}) => api.request({ url, ...opts, _online: true });

const pool = async (tasks, n, onStep) => {
  let i = 0, done = 0;
  await Promise.all(Array.from({ length: n }, async () => {
    while (i < tasks.length) { const t = tasks[i++]; await t().catch(() => {}); done += 1; onStep(done, tasks.length); }
  }));
};

const setBusy = (uid, v) => { S.busy = { ...S.busy, [uid]: v }; if (!v) delete S.busy[uid]; emit(); };

export const download = async (uid) => {
  const r = {};
  const grab = async (url, opts) => { const { key } = splitUrl(url, opts?.params); const res = await online(url, opts); r[key] = res.data; return res.data; };
  setBusy(uid, { done: 0, total: 1 });
  try {
    for (const u of ["/auth/me", "/billing/me", "/universes"]) { const res = await online(u).catch(() => null); if (res) record("_g", u, res.data); }
    const uni = await grab(`/universes/${uid}`);
    const L = (p) => grab(`/universes/${uid}/${p}`).catch(() => []);
    const [ents, stories, tls, maps, langs, cals] = await Promise.all(["entities", "stories", "timelines", "maps", "languages", "calendars"].map(L));
    await Promise.all(["scenes", "jumps", "quotes", "quotes/tags", "events/all", "relationship-web", "members", "dismissed-suggestions", "suggestions", "comment-counts"].map(L));
    const tasks = [];
    const add = (url, opts) => tasks.push(() => grab(url, opts));
    (ents || []).forEach((e) => add(`/entities/${e.id}`));
    (Array.isArray(stories) ? stories : stories?.stories || []).forEach((st) => tasks.push(async () => {
      const full = await grab(`/stories/${st.id}`);
      const end = new Date(Date.now() + 864e5).toISOString().slice(0, 13);
      await online(`/stories/${st.id}/stats`, { params: { start: "2000-01-01T00", end } }).then((x) => { r[`/stories/${st.id}/stats`] = x.data; }).catch(() => {});
      if (full.has_cover) await grab(`/stories/${st.id}/cover`, { responseType: "blob" }).catch(() => {});
      for (const d of full.drafts || []) {
        const items = await grab(`/drafts/${d.id}/items`).catch(() => []);
        await Promise.all((Array.isArray(items) ? items : items?.items || []).map((it) => grab(`/items/${it.id}`).catch(() => {})));
      }
    }));
    (tls || []).forEach((t) => { add(`/timelines/${t.id}/events`); add(`/timelines/${t.id}/calendar-dates`); });
    (maps || []).forEach((m) => { add(`/maps/${m.id}/pins`); add(`/maps/${m.id}/regions`); if (m.kind !== "painted") add(`/maps/${m.id}/image`, { responseType: "blob" }); });
    (langs || []).forEach((l) => { add(`/languages/${l.id}`); add(`/languages/${l.id}/words`); add(`/languages/${l.id}/glyphs`); });
    (cals || []).forEach((c) => {
      add(`/calendars/${c.id}/events`);
      if (c.today) [-1, 0, 1].forEach((d) => { const n = c.month_names?.length || 12, m = c.today.month + d; add(`/calendars/${c.id}/month?year=${c.today.year + (m < 0 ? -1 : m >= n ? 1 : 0)}&month=${(m + n) % n}`); });
    });
    await pool(tasks, 6, (done, total) => setBusy(uid, { done, total }));
    S.unis[uid] = { r };
    reindex(uid);
    S.meta = { ...S.meta, [uid]: { name: uni.name, at: new Date().toISOString(), count: Object.keys(r).length } };
    persist(`u:${uid}`); persist("meta"); persist("u:_g");
  } finally { setBusy(uid, null); }
};

export const cacheLocal = (id, patch) => {
  const uid = S.index[id];
  if (!uid || !S.unis[uid] || pendingFor(uid).length) return;
  Object.values(S.unis[uid].r).forEach((v) => walk(v, (o) => { if (o.id === id) Object.assign(o, patch); }));
  persist(`u:${uid}`);
};

export const remove = async (uid) => {
  S.outbox = S.outbox.filter((e) => e.uid !== uid);
  const { [uid]: _, ...rest } = S.meta;
  S.meta = rest;
  delete S.unis[uid];
  persist("outbox"); persist("meta"); persist(`u:${uid}`);
  emit();
};

export const setManual = (v) => {
  S.manual = v;
  localStorage.setItem("lf_offline_manual", v ? "1" : "0");
  emit();
  window.dispatchEvent(new Event("lf-offline-mode"));
};

export const discard = (ids) => {
  const drop = new Set(ids);
  S.outbox.filter((e) => drop.has(e.id)).forEach((e) => { if (S.meta[e.uid]) S.meta[e.uid] = { ...S.meta[e.uid], stale: true }; });
  S.outbox = S.outbox.filter((e) => !drop.has(e.id));
  persist("outbox"); persist("meta");
  emit();
  refreshStale();
};

export const refreshStale = async () => {
  if (!navigator.onLine || S.auto) return;
  for (const u of Object.keys(S.meta)) {
    if (S.meta[u]?.stale && !pendingFor(u).length && !S.busy[u]) await download(u).catch(() => {});
  }
  window.dispatchEvent(new Event("lf-offline-mode"));
};

const MERGEABLE = /^\/(entities|items|stories|scenes|languages|words|timelines|events|calendars)\/[^/]+$/;

const pushOne = async (e) => {
  const url = replaceUrl(e.path, S.idmap);
  if (segs(url).some((p) => p.startsWith("tmp-"))) throw new Error("Depends on an item that couldn't be created");
  let body = replaceIds(e.body, S.idmap);
  if (e.method === "patch" && e.base && MERGEABLE.test(url)) {
    const theirs = (await online(url).catch(() => null))?.data;
    if (theirs) {
      body = { ...body };
      Object.keys(body).forEach((k) => {
        if (typeof body[k] === "string" && typeof theirs[k] === "string" && typeof e.base[k] === "string" && theirs[k] !== e.base[k]) body[k] = merge3(e.base[k], body[k], theirs[k]);
      });
    }
  }
  const res = await online(url, { method: e.method, data: e.method === "delete" ? undefined : body });
  if (e.tmp && res.data?.id) { S.idmap = { ...S.idmap, [e.tmp]: res.data.id }; persist("idmap"); }
};

export const push = async (uid) => {
  if (S.pushing) return { pushed: 0, failed: 0 };
  S.pushing = true; emit();
  let pushed = 0, failed = 0, stopped = false;
  try {
    for (const e of [...S.outbox].filter((x) => !uid || x.uid === uid)) {
      try {
        await pushOne(e);
        S.outbox = S.outbox.filter((x) => x.id !== e.id);
        pushed += 1;
      } catch (err) {
        if (err.isAxiosError && !err.response) { stopped = true; break; }
        const detail = err.response?.data?.detail;
        S.outbox = S.outbox.map((x) => (x.id === e.id ? { ...x, error: typeof detail === "string" ? detail : err.message || "Couldn't push this change" } : x));
        failed += 1;
      }
      persist("outbox"); emit();
    }
    const unis = [...new Set(uid ? [uid] : Object.keys(S.meta))];
    if (!stopped) for (const u of unis) if (S.meta[u] && !pendingFor(u).length) await download(u).catch(() => {});
  } finally { S.pushing = false; emit(); window.dispatchEvent(new Event("lf-offline-mode")); }
  return { pushed, failed, stopped };
};

export const refreshAll = async () => { for (const u of Object.keys(S.meta)) if (!pendingFor(u).length) await download(u).catch(() => {}); };

window.addEventListener("online", () => { emit(); window.dispatchEvent(new Event("lf-offline-mode")); refreshStale(); });
window.addEventListener("offline", () => { emit(); window.dispatchEvent(new Event("lf-offline-mode")); });
